import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, Subject } from 'rxjs';
import { throttleTime, map } from 'rxjs/operators';

/**
 * Service to track user activity for intelligent token refresh decisions.
 * Only refreshes tokens for users who have been active in the last 60 minutes.
 * Automatically logs out users after 60 minutes of inactivity.
 */
@Injectable({
  providedIn: 'root'
})
export class UserActivityService {
  private lastActivityTime: number = Date.now();
  private isActiveSubject = new BehaviorSubject<boolean>(true);
  private readonly ACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
  private readonly IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes for security timeout
  private readonly CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds
  private checkIntervalId: any;
  private idleTimeoutTriggered = false; // Flag to prevent multiple idle timeout events

  // Emit when user has been idle for 60 minutes
  private idleTimeoutSubject = new Subject<void>();
  public idleTimeout$ = this.idleTimeoutSubject.asObservable();

  public isActive$: Observable<boolean> = this.isActiveSubject.asObservable();

  constructor(private ngZone: NgZone) {
    this.initActivityTracking();
  }

  /**
   * Initialize activity tracking for mouse and keyboard events
   */
  private initActivityTracking(): void {
    // Run outside Angular zone to avoid triggering change detection on every mouse move
    this.ngZone.runOutsideAngular(() => {
      // Track various user interactions
      const userInteractions$ = merge(
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart'),
        fromEvent(document, 'click')
      ).pipe(
        throttleTime(1000), // Only process one event per second
        map(() => Date.now())
      );

      userInteractions$.subscribe((timestamp) => {
        this.updateActivityTime(timestamp);
      });

      // Periodically check if user is still active
      this.checkIntervalId = setInterval(() => {
        this.checkActivityStatus();
      }, this.CHECK_INTERVAL_MS);
    });
  }

  /**
   * Update the last activity timestamp
   */
  private updateActivityTime(timestamp: number): void {
    this.lastActivityTime = timestamp;

    // Reset idle timeout flag when user becomes active
    if (this.idleTimeoutTriggered) {
      this.idleTimeoutTriggered = false;
    }

    // If user was inactive and just became active, update the subject
    if (!this.isActiveSubject.value) {
      this.ngZone.run(() => {
        this.isActiveSubject.next(true);
        // console.log('👤 User became active');
      });
    }
  }

  /**
   * Check if user is still considered active based on timeout
   */
  private checkActivityStatus(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    const isActive = timeSinceLastActivity < this.ACTIVITY_TIMEOUT_MS;
    const isIdleTooLong = timeSinceLastActivity >= this.IDLE_TIMEOUT_MS;

    // Check if user has been idle too long - trigger logout (only once)
    if (isIdleTooLong && !this.idleTimeoutTriggered) {
      this.idleTimeoutTriggered = true;
      const minutes = Math.floor(timeSinceLastActivity / 60000);
      const seconds = Math.floor(timeSinceLastActivity / 1000);
      console.log(`⏰ User idle for ${seconds}s - triggering idle timeout`);
      this.ngZone.run(() => {
        this.idleTimeoutSubject.next();
      });
      return;
    }

    // Only update if status changed
    if (isActive !== this.isActiveSubject.value) {
      this.ngZone.run(() => {
        this.isActiveSubject.next(isActive);
        if (isActive) {
          // console.log('👤 User is active (within 60 minutes)');
        } else {
          // console.log('💤 User is inactive (no activity for 60+ minutes)');
        }
      });
    }
  }

  /**
   * Check if user has been active within the specified time window
   * @param timeWindowMs Time window in milliseconds (default: 60 minutes)
   * @returns true if user was active within the time window
   */
  public isUserActive(timeWindowMs: number = this.ACTIVITY_TIMEOUT_MS): boolean {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    return timeSinceLastActivity < timeWindowMs;
  }

  /**
   * Get the timestamp of the last user activity
   */
  public getLastActivityTime(): number {
    return this.lastActivityTime;
  }

  /**
   * Get time elapsed since last activity in milliseconds
   */
  public getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Get time elapsed since last activity in a human-readable format
   */
  public getTimeSinceLastActivityFormatted(): string {
    const elapsed = this.getTimeSinceLastActivity();
    const minutes = Math.floor(elapsed / 60000);
    const seconds = Math.floor((elapsed % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  /**
   * Check if user has been idle for more than the configured idle timeout.
   * @returns true if user has been idle longer than the configured threshold.
   */
  public isUserIdleTooLong(): boolean {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    return timeSinceLastActivity >= this.IDLE_TIMEOUT_MS;
  }

  /**
   * Get the idle timeout duration in milliseconds.
   */
  public getIdleTimeoutMs(): number {
    return this.IDLE_TIMEOUT_MS;
  }

  /**
   * Manually mark user as active (useful for programmatic actions)
   */
  public markUserActive(): void {
    this.updateActivityTime(Date.now());
  }

  /**
   * Reset activity tracking
   */
  public reset(): void {
    this.lastActivityTime = Date.now();
    this.idleTimeoutTriggered = false;
    this.isActiveSubject.next(true);
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
    }
  }
}
